import { useState } from 'react';
import betClient from '../../api/betClient';
import { useEffectiveTier } from '../../hooks/useEffectiveTier';
import { renderInlineMarkdown } from '../../utils/renderMarkdown';
import UpgradeModal from '../upgrade/UpgradeModal';

export default function AiGamePreview({ eventId, sport }) {
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const tier = useEffectiveTier();
  const [modalOpen, setModalOpen] = useState(false);
  const locked = tier === 'free';

  function fetchPreview(force = false) {
    setLoading(true); setError(null);
    betClient.get(`/api/ai/game-preview/${eventId}?sport=${sport}${force ? '&force=true' : ''}`)
      .then((r) => setAnalysis(r.data))
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }

  return (
    <div className="rounded border-l-[3px] border-l-[#005BB3] dark:border-l-[#3b82f6] border border-[#E5E7EB] dark:border-[#1e2536] bg-[#F8F9FA] dark:bg-[#0f1219] px-5 py-4">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <h3 className="text-[12px] uppercase tracking-[1px] font-medium text-[#005BB3] dark:text-[#3b82f6]" style={{ fontFamily: 'var(--font-sans)' }}>AI Analysis</h3>
        </div>
        {analysis && !loading && !locked && (
          <button onClick={() => fetchPreview(true)} className="text-[10px] text-[#3b82f6] hover:text-[#2563eb] transition-colors duration-150">Regenerate</button>
        )}
      </div>

      {!analysis && !loading && !error && (
        <>
          <p className="text-[#6B7280] dark:text-[#8892a8] text-[11px] mb-3">AI-powered preview analyzing spreads, line movement, and value.</p>
          {locked ? (
            <button onClick={() => setModalOpen(true)}
              className="px-3 py-1.5 rounded bg-[#94a3b8] text-white text-[11px] font-medium cursor-pointer flex items-center gap-1.5 opacity-80 hover:opacity-100 transition-opacity duration-150">
              Generate Preview
              <span className="text-[9px] font-bold px-1 py-px rounded bg-white/20 uppercase">Pro</span>
            </button>
          ) : (
            <button onClick={() => fetchPreview(false)} className="px-3 py-1.5 rounded bg-[#3b82f6] hover:bg-[#2563eb] text-white text-[11px] font-medium transition-colors duration-150">Generate Preview</button>
          )}
        </>
      )}

      {loading && (
        <div className="flex items-center gap-2 py-3">
          <svg className="animate-spin h-3.5 w-3.5 text-[#3b82f6]" viewBox="0 0 24 24" fill="none">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          <span className="text-[#8892a8] text-[11px]">Generating...</span>
        </div>
      )}

      {error && (
        <div className="rounded bg-[rgba(239,68,68,0.06)] dark:bg-[rgba(239,68,68,0.08)] px-3 py-2 mt-1">
          <p className="text-[#ef4444] text-[11px]">Failed to load.</p>
          <button onClick={() => fetchPreview(false)} className="text-[10px] text-[#ef4444] underline mt-1">Retry</button>
        </div>
      )}

      {analysis && !loading && (
        <div>
          {(analysis.content || '').split('\n\n').map((para, i) => (
            <p key={i} className="text-[#1F2937] dark:text-[#8892a8] text-[14px] leading-relaxed mb-2" style={{ fontFamily: 'var(--font-sans)' }}>{renderInlineMarkdown(para)}</p>
          ))}
          <div className="flex items-center gap-2 mt-3 pt-2 border-t border-[#E5E7EB] dark:border-[#1e2536]">
            {analysis.cached && <span className="text-[9px] font-semibold px-1 py-px rounded bg-[#f1f5f9] dark:bg-[#1a2030] text-[#8892a8]">CACHED</span>}
            <span className="text-[10px] text-[#505a6e]" style={{ fontFamily: 'var(--font-mono)' }}>{new Date(analysis.created_at).toLocaleString()}</span>
          </div>
        </div>
      )}

      <UpgradeModal open={modalOpen} onClose={() => setModalOpen(false)} suggestedTier="pro" triggerFeature="Game Preview" />
    </div>
  );
}
